import * as R from 'ramda';
import * as P from 'plow-js';
import { createReducer } from 'redux-act';
// features
import { socketTelWarningReceived, socketDriverCardsTelReceived } from '../sockets/actions';
// helpers/constants
import * as G from '../../helpers';
import * as GC from '../../constants';
// feature drivers-card
import * as A from './actions';
import { mapDriverLoadsToEmptyPeriods, filterDriverLoadsByLocationAndDate } from './helpers';
import {
  ZOOM_TYPE_1X,
  CARD_HEIGHT_170,
  GROUP_TYPE_NONE,
  FILTER_TYPE_NONE,
  GROUP_TYPE_DISPATCHER,
  GROUP_TYPE_GROUP_AND_DISPATCHER,
} from './constants';
//////////////////////////////////////////////////

const initialState = {
  openedGroups: [],
  searchDate: null,
  zoom: ZOOM_TYPE_1X,
  unassignedLoads: [],
  telNotifications: {},
  driverCardsInfo: null,
  dispatchByTruck: false,
  globalFilterValue: null,
  searchRadiusValue: null,
  minEmptyHoursValue: null,
  groupBy: GROUP_TYPE_NONE,
  loadsByDispatchGroup: {},
  loadRefFilterValue: null,
  showUnassignedLoads: false,
  cardHeight: CARD_HEIGHT_170,
  notGroupedAssignedLoads: [],
  driverLoadsByDispatcher: {},
  searchLoadsByCustomerFilter: null,
  searchPlaceAutocompleteValue: null,
  searchPlaceAutocompleteResult: null,
  filteredByPlaceAutocompleteLoads: null,
  loadsByDispatchGroupAndDispatchers: {},
  notGroupedAssignedLoadsPagination: {
    items: [],
    limit: 50,
    offset: 0,
    totalCount: 0,
  },
  filters: {
    dateTo: null,
    dateFrom: null,
    initialFilterDays: null,
    filter: FILTER_TYPE_NONE,
  },
};

const setInitialState = () => initialState;

const setValueToStore = (state, { path, value }) => P.$set(path, value, state);

const toggleUnassignedLoads = (state) => P.$toggle('showUnassignedLoads', state);

const toggleDispatchByTruck = (state) => P.$toggle('dispatchByTruck', state);

const setGroupByToStore = (state, data) => P.$set('groupBy', data, state);

const setZoomToStore = (state, data) => P.$set('zoom', data, state);

const setFiltersToStore = (state, data) => P.$set('filters', data, state);

const setSearchByCustomerFilter = (state, customer) =>
  P.$set('searchLoadsByCustomerFilter', customer, state);

const setGlobalFilterValue = (state, data) => P.$set('globalFilterValue', data, state);

const setLoadRefFilterValue = (state, data) => P.$set('loadRefFilterValue', data, state);

const setFilterToStore = (state, data) => P.$set('filters.filter', data, state);

const setDayFilterToStore = (state, data) => P.$set('filters.initialFilterDays', data, state);

const getUnassignedLoadsSuccess = (state, data) => P.$set('unassignedLoads', data, state);

const getDriverCardsInfoSuccess = (state, data) => P.$set('driverCardsInfo', data, state);

const getDriverCardsByTruckInfoSuccess = (state, data) => P.$set('driverCardsByTruckInfo', data, state);

const createDriverOnDriversCardSuccess = (state, data) => {
  const { telGuid } = data;

  const { unassignedLoads } = state;

  const newUnassignedLoads = R.reject(R.propEq(telGuid, GC.FIELD_GUID), unassignedLoads);

  return P.$set('unassignedLoads', newUnassignedLoads, state);
};

// GROUPED

const getDriverLoadsByDispatcherSuccess = (state, {
  data,
  dispatcherGuid,
}) => {
  if (G.isNilOrEmpty(dispatcherGuid)) {
    const loadsByDispatchGroup = R.reduce((acc, obj) => {
      const groupKey = obj.groupByGuid;

      if (G.isNilOrEmpty(groupKey)) return acc;

      const existed = R.prop(groupKey, acc);

      if (G.isNotNilAndNotEmpty(existed)) return R.assoc(groupKey, R.append(obj, existed), acc);

      return R.assoc(groupKey, [obj], acc);
    }, {}, data);

    return P.$all(
      P.$set('searchDate', null),
      P.$set('searchPlaceAutocompleteValue', null),
      P.$set('searchPlaceAutocompleteResult', null),
      P.$set('driverLoadsByDispatcher', loadsByDispatchGroup),
      state,
    );
  }

  return P.$all(
    P.$set('searchDate', null),
    P.$set('searchPlaceAutocompleteValue', null),
    P.$set('searchPlaceAutocompleteResult', null),
    P.$set(`driverLoadsByDispatcher.${dispatcherGuid}`, data),
    state,
  );
};

const getLoadsByDispatchGroupSuccess = (state, {
  data,
  dispatchingGroupGuid,
}) => {
  if (G.isNilOrEmpty(dispatchingGroupGuid)) {
    const loadsByDispatchGroup = R.reduce((acc, obj) => {
      const groupKey = obj.groupByGuid;

      if (G.isNilOrEmpty(groupKey)) return acc;

      const existed = R.prop(groupKey, acc);

      if (G.isNotNilAndNotEmpty(existed)) return R.assoc(groupKey, R.append(obj, existed), acc);

      return R.assoc(groupKey, [obj], acc);
    }, {}, data);

    return P.$all(
      P.$set('searchDate', null),
      P.$set('searchPlaceAutocompleteValue', null),
      P.$set('searchPlaceAutocompleteResult', null),
      P.$set('loadsByDispatchGroup', loadsByDispatchGroup),
      state,
    );
  }

  return P.$all(
    P.$set('searchDate', null),
    P.$set('searchPlaceAutocompleteValue', null),
    P.$set('searchPlaceAutocompleteResult', null),
    P.$set(`loadsByDispatchGroup.${dispatchingGroupGuid}`, data),
    state,
  );
};

// GROUP_TYPE_GROUP_AND_DISPATCHER
const getLoadsByDispatchGroupAndDispatcherSuccess = (state, {
  data,
  dispatcherGuid,
  dispatchingGroupGuid,
}) => {
  if (R.and(G.isNilOrEmpty(dispatcherGuid), G.isNilOrEmpty(dispatchingGroupGuid))) {
    const loadsByDispatchGroupAndDispatchers = R.reduce((acc, obj) => {
      const groupKey = obj.groupByGuid;
      const dispatcherKey = obj.assignedDispatcherGuid;

      if (R.or(G.isNilOrEmpty(groupKey), G.isNilOrEmpty(dispatcherKey))) return acc;

      const existed = R.path([groupKey, dispatcherKey], acc);

      if (G.isNotNilAndNotEmpty(existed)) return R.assocPath([groupKey, dispatcherKey], R.append(obj, existed), acc);

      return R.assocPath([groupKey, dispatcherKey], [obj], acc);
    }, {}, data);

    const openedGroups = R.compose(
      R.uniq,
      R.reduce((acc, obj) => {
        const groupKey = obj.groupByGuid;

        if (G.isNilOrEmpty(groupKey)) return acc;

        return R.append(groupKey, acc);
      }, {}),
    )(data);

    return P.$all(
      P.$set('searchDate', null),
      P.$set('openedGroups', openedGroups),
      P.$set('searchPlaceAutocompleteValue', null),
      P.$set('searchPlaceAutocompleteResult', null),
      P.$set('loadsByDispatchGroupAndDispatchers', loadsByDispatchGroupAndDispatchers),
      state,
    );
  }

  return P.$all(
    P.$set('searchDate', null),
    P.$set('searchPlaceAutocompleteValue', null),
    P.$set('searchPlaceAutocompleteResult', null),
    P.$set(`loadsByDispatchGroupAndDispatchers.${dispatchingGroupGuid}.${dispatcherGuid}`, data),
    state,
  );
};

const dropLoadsByDispatchGroupAndDispatchersGroup = (state, dispatchingGroupGuid) => (
  P.$drop(`loadsByDispatchGroupAndDispatchers.${dispatchingGroupGuid}`, state));
// GROUP_TYPE_GROUP_AND_DISPATCHER

const getNotGroupedAssignedLoadsSuccess = (state, data) => {
  const { notGroupedAssignedLoadsPagination } = state;
  const { limit } = notGroupedAssignedLoadsPagination;

  const driverLoads = data;

  const taken = R.take(limit, driverLoads);

  return P.$all(
    P.$set('searchDate', null),
    P.$set('notGroupedAssignedLoads', taken),
    P.$set('searchPlaceAutocompleteValue', null),
    P.$set('searchPlaceAutocompleteResult', null),
    P.$set('filteredByPlaceAutocompleteLoads', null),
    P.$set('notGroupedAssignedLoadsPagination.offset', limit),
    P.$set('notGroupedAssignedLoadsPagination.items', driverLoads),
    P.$set('notGroupedAssignedLoadsPagination.totalCount', R.length(driverLoads)),
    state,
  );
};

// TODO: check real pagination with API
const getMoreNotGroupedAssignedLoads = (state) => {
  const { notGroupedAssignedLoadsPagination } = state;
  const { items, limit, offset, totalCount } = notGroupedAssignedLoadsPagination;

  let newOffset = R.add(offset, limit);

  if (R.lt(R.subtract(totalCount, newOffset), limit)) {
    newOffset = totalCount;
  }

  const taken = R.take(newOffset, items);

  return P.$all(
    P.$set('notGroupedAssignedLoads', taken),
    P.$set('notGroupedAssignedLoadsPagination.offset', newOffset),
    state,
  );
};

const setDateRangeFilters = (state, { dateTo, dateFrom }) => (
  P.$all(
    P.$set('filters.dateTo', dateTo),
    P.$set('filters.dateFrom', dateFrom),
    state,
  )
);

const filterByPlaceAutocomplete = (state, result) => {
  const {
    filters,
    searchDate,
    searchRadiusValue,
    minEmptyHoursValue,
    searchPlaceAutocompleteResult,
    notGroupedAssignedLoadsPagination,
  } = state;

  const { items } = notGroupedAssignedLoadsPagination;

  const resultToUse = R.or(result, searchPlaceAutocompleteResult);
  const address = G.getPropFromObject('formattedAddress', resultToUse);

  const mapped = mapDriverLoadsToEmptyPeriods(items, minEmptyHoursValue, filters);

  const filtered = filterDriverLoadsByLocationAndDate(mapped, resultToUse, searchRadiusValue, searchDate);

  return P.$all(
    P.$set('searchPlaceAutocompleteValue', address),
    P.$set('searchPlaceAutocompleteResult', resultToUse),
    P.$set('filteredByPlaceAutocompleteLoads', filtered),
    state,
  );
};

const setSearchPlaceAutocompleteValue = (state, value) => {
  if (G.isNilOrEmpty(value)) {
    return P.$all(
      P.$set('searchPlaceAutocompleteValue', null),
      P.$set('searchPlaceAutocompleteResult', null),
      P.$set('filteredByPlaceAutocompleteLoads', null),
      state,
    );
  }

  return P.$set('searchPlaceAutocompleteValue', value, state);
};

const setSearchRadiusValue = (state, value) => P.$set('searchRadiusValue', value, state);

const setMinEmptyHoursValue = (state, value) => P.$set('minEmptyHoursValue', value, state);

const setSearchDateToStore = (state, value) => {
  if (G.isNilOrEmpty(value)) {
    return P.$all(
      P.$set('searchDate', value),
      P.$set('filteredByPlaceAutocompleteLoads', null),
      state,
    );
  }

  if (G.isValidDate(value)) {
    const {
      filters,
      searchRadiusValue,
      minEmptyHoursValue,
      searchPlaceAutocompleteResult,
      notGroupedAssignedLoadsPagination,
    } = state;

    const { items } = notGroupedAssignedLoadsPagination;

    const mapped = mapDriverLoadsToEmptyPeriods(items, minEmptyHoursValue, filters);

    const filtered = filterDriverLoadsByLocationAndDate(
      mapped,
      searchPlaceAutocompleteResult,
      searchRadiusValue,
      value,
    );

    return P.$all(
      P.$set('searchDate', value),
      P.$set('filteredByPlaceAutocompleteLoads', filtered),
      state,
    );
  }

  return P.$set('searchDate', value, state);
};

// pending activity
const createPendingActivitySuccess = (state, data) => {
  const { groupBy, dispatchByTruck } = state;

  const { groupGuid, truckGuid, driverGuid, primaryDriver, dispatcherGuid } = data;

  const mapper = (item) => {
    const guid = G.getGuidFromObject(item);

    if (R.equals(guid, driverGuid)) return P.$add('pendingActivities', data, item);

    return item;
  };

  const truckMapper = (item) => {
    const guid = G.getGuidFromObject(item);

    if (R.equals(guid, truckGuid)) {
      if (G.isTrue(primaryDriver)) return P.$add('primaryDriverPendingActivities', data, item);

      return P.$add('secondaryDriverPendingActivities', data, item);
    }

    return item;
  };

  const mapperToUse = G.isTrue(dispatchByTruck) ? truckMapper : mapper;

  if (G.isAllTrue(
    G.isTrue(dispatchByTruck),
    G.isNotNilAndNotEmpty(groupGuid),
    R.equals(groupBy, GROUP_TYPE_GROUP_AND_DISPATCHER),
  )) {
    const groupStateProp = 'loadsByDispatchGroupAndDispatchers';

    const newGroupLoads = P.$map(
      mapperToUse,
      `${groupStateProp}.${groupGuid}.${dispatcherGuid}`,
      state,
    );

    return P.$set(`${groupStateProp}.${groupGuid}.${dispatcherGuid}`, newGroupLoads, state);
  }

  if (R.and(G.isNotNilAndNotEmpty(groupBy), G.isNotNilAndNotEmpty(groupGuid))) {
    const groupStateProp = G.ifElse(
      R.equals(groupBy, GROUP_TYPE_DISPATCHER),
      'driverLoadsByDispatcher',
      'loadsByDispatchGroup',
    );

    const newGroupLoads = P.$map(
      mapperToUse,
      `${groupStateProp}.${groupGuid}`,
      state,
    );

    return P.$set(`${groupStateProp}.${groupGuid}`, newGroupLoads, state);
  }

  const newLoads = P.$map(
    mapperToUse,
    'notGroupedAssignedLoads',
    state,
  );

  const newItems = P.$map(
    mapperToUse,
    'notGroupedAssignedLoadsPagination.items',
    state,
  );

  return P.$all(
    P.$set('notGroupedAssignedLoads', newLoads),
    P.$set('notGroupedAssignedLoadsPagination.items', newItems),
    state,
  );
};

const updatePendingActivitySuccess = (state, data) => {
  const { groupBy, dispatchByTruck } = state;

  const { groupGuid, truckGuid, driverGuid, dispatcherGuid } = data;

  const activityGuid = G.getGuidFromObject(data);

  const mapper = (item) => {
    const guid = G.getGuidFromObject(item);

    if (R.equals(guid, driverGuid)) {
      const { pendingActivities } = item;

      return R.assoc(
        'pendingActivities',
        R.map((activity) => {
          const acGuid = G.getGuidFromObject(activity);

          if (R.equals(acGuid, activityGuid)) return data;

          return activity;
        }, pendingActivities),
        item,
      );
    }

    return item;
  };

  const truckMapper = (item) => {
    const guid = G.getGuidFromObject(item);

    if (R.equals(guid, truckGuid)) {
      const { primaryDriverPendingActivities, secondaryDriverPendingActivities } = item;

      return {
        ...item,
        primaryDriverPendingActivities: R.map((activity) => {
          const acGuid = G.getGuidFromObject(activity);

          if (R.equals(acGuid, activityGuid)) return data;

          return activity;
        }, R.or(primaryDriverPendingActivities, [])),
        secondaryDriverPendingActivities: R.map((activity) => {
          const acGuid = G.getGuidFromObject(activity);

          if (R.equals(acGuid, activityGuid)) return data;

          return activity;
        }, R.or(secondaryDriverPendingActivities, [])),
      };
    }

    return item;
  };

  const mapperToUse = G.isTrue(dispatchByTruck) ? truckMapper : mapper;

  if (G.isAllTrue(
    G.isTrue(dispatchByTruck),
    G.isNotNilAndNotEmpty(groupGuid),
    R.equals(groupBy, GROUP_TYPE_GROUP_AND_DISPATCHER),
  )) {
    const groupStateProp = 'loadsByDispatchGroupAndDispatchers';

    const newGroupLoads = P.$map(
      mapperToUse,
      `${groupStateProp}.${groupGuid}.${dispatcherGuid}`,
      state,
    );

    return P.$set(`${groupStateProp}.${groupGuid}.${dispatcherGuid}`, newGroupLoads, state);
  }

  if (R.and(G.isNotNilAndNotEmpty(groupBy), G.isNotNilAndNotEmpty(groupGuid))) {
    const groupStateProp = G.ifElse(
      R.equals(groupBy, GROUP_TYPE_DISPATCHER),
      'driverLoadsByDispatcher',
      'loadsByDispatchGroup',
    );

    const newGroupLoads = P.$map(
      mapperToUse,
      `${groupStateProp}.${groupGuid}`,
      state,
    );

    return P.$set(`${groupStateProp}.${groupGuid}`, newGroupLoads, state);
  }

  const newLoads = P.$map(
    mapperToUse,
    'notGroupedAssignedLoads',
    state,
  );

  const newItems = P.$map(
    mapperToUse,
    'notGroupedAssignedLoadsPagination.items',
    state,
  );

  return P.$all(
    P.$set('notGroupedAssignedLoads', newLoads),
    P.$set('notGroupedAssignedLoadsPagination.items', newItems),
    state,
  );
};

const removePendingActivitySuccess = (state, data) => {
  const { groupBy, dispatchByTruck } = state;

  const { groupGuid, truckGuid, driverGuid, primaryDriver, dispatcherGuid } = data;

  const activityGuid = G.getGuidFromObject(data);

  const mapper = (item) => {
    const { pendingActivities } = item;

    const drGuid = G.getGuidFromObject(item);

    if (R.equals(drGuid, driverGuid)) {
      return R.assoc(
        'pendingActivities',
        R.reject(R.propEq(activityGuid, GC.FIELD_GUID), pendingActivities),
        item,
      );
    }

    return item;
  };

  const truckMapper = (item) => {
    const { primaryDriverPendingActivities, secondaryDriverPendingActivities } = item;

    const drGuid = G.getGuidFromObject(item);

    if (R.equals(drGuid, truckGuid)) {
      if (G.isTrue(primaryDriver)) {
        return R.assoc(
          'primaryDriverPendingActivities',
          R.reject(R.propEq(activityGuid, GC.FIELD_GUID), primaryDriverPendingActivities),
          item,
        );
      }

      return R.assoc(
        'secondaryDriverPendingActivities',
        R.reject(R.propEq(activityGuid, GC.FIELD_GUID), secondaryDriverPendingActivities),
        item,
      );
    }

    return item;
  };

  const mapperToUse = G.isTrue(dispatchByTruck) ? truckMapper : mapper;

  if (G.isAllTrue(
    G.isTrue(dispatchByTruck),
    G.isNotNilAndNotEmpty(groupGuid),
    R.equals(groupBy, GROUP_TYPE_GROUP_AND_DISPATCHER),
  )) {
    const groupStateProp = 'loadsByDispatchGroupAndDispatchers';

    const newGroupLoads = P.$map(
      mapperToUse,
      `${groupStateProp}.${groupGuid}.${dispatcherGuid}`,
      state,
    );

    return P.$set(`${groupStateProp}.${groupGuid}.${dispatcherGuid}`, newGroupLoads, state);
  }

  if (R.and(G.isNotNilAndNotEmpty(groupBy), G.isNotNilAndNotEmpty(groupGuid))) {
    const groupStateProp = G.ifElse(
      R.equals(groupBy, GROUP_TYPE_DISPATCHER),
      'driverLoadsByDispatcher',
      'loadsByDispatchGroup',
    );

    const newGroupLoads = P.$map(
      mapperToUse,
      `${groupStateProp}.${groupGuid}`,
      state,
    );

    return P.$set(`${groupStateProp}.${groupGuid}`, newGroupLoads, state);
  }

  const newLoads = P.$map(
    mapperToUse,
    'notGroupedAssignedLoads',
    state,
  );

  const newItems = P.$map(
    mapperToUse,
    'notGroupedAssignedLoadsPagination.items',
    state,
  );

  return P.$all(
    P.$set('notGroupedAssignedLoads', newLoads),
    P.$set('notGroupedAssignedLoadsPagination.items', newItems),
    state,
  );
};

// unavailable period
const createUnavailablePeriodSuccess = (state, data) => {
  const { groupBy, dispatchByTruck } = state;

  const { groupGuid, truckGuid, driverGuid, primaryDriver, dispatcherGuid } = data;

  const mapper = (item) => {
    const guid = G.getGuidFromObject(item);

    if (R.equals(guid, driverGuid)) return P.$add('unavailablePeriods', data, item);

    return item;
  };

  const truckMapper = (item) => {
    const guid = G.getGuidFromObject(item);

    if (R.equals(guid, truckGuid)) {
      if (G.isTrue(primaryDriver)) return P.$add('primaryDriverUnavailablePeriods', data, item);

      return P.$add('secondaryDriverUnavailablePeriods', data, item);
    }

    return item;
  };

  const mapperToUse = G.isTrue(dispatchByTruck) ? truckMapper : mapper;

  if (G.isAllTrue(
    G.isTrue(dispatchByTruck),
    G.isNotNilAndNotEmpty(groupGuid),
    R.equals(groupBy, GROUP_TYPE_GROUP_AND_DISPATCHER),
  )) {
    const groupStateProp = 'loadsByDispatchGroupAndDispatchers';

    const newGroupLoads = P.$map(
      mapperToUse,
      `${groupStateProp}.${groupGuid}.${dispatcherGuid}`,
      state,
    );

    return P.$set(`${groupStateProp}.${groupGuid}.${dispatcherGuid}`, newGroupLoads, state);
  }

  if (R.and(G.isNotNilAndNotEmpty(groupBy), G.isNotNilAndNotEmpty(groupGuid))) {
    const groupStateProp = G.ifElse(
      R.equals(groupBy, GROUP_TYPE_DISPATCHER),
      'driverLoadsByDispatcher',
      'loadsByDispatchGroup',
    );

    const newGroupLoads = P.$map(
      mapperToUse,
      `${groupStateProp}.${groupGuid}`,
      state,
    );

    return P.$set(`${groupStateProp}.${groupGuid}`, newGroupLoads, state);
  }

  const newLoads = P.$map(
    mapperToUse,
    'notGroupedAssignedLoads',
    state,
  );

  const newItems = P.$map(
    mapperToUse,
    'notGroupedAssignedLoadsPagination.items',
    state,
  );

  return P.$all(
    P.$set('notGroupedAssignedLoads', newLoads),
    P.$set('notGroupedAssignedLoadsPagination.items', newItems),
    state,
  );
};

const updateUnavailablePeriodSuccess = (state, data) => {
  const { groupBy, dispatchByTruck } = state;

  const { groupGuid, truckGuid, driverGuid, dispatcherGuid } = data;

  const activityGuid = G.getGuidFromObject(data);

  const mapper = (item) => {
    const guid = G.getGuidFromObject(item);

    if (R.equals(guid, driverGuid)) {
      const { unavailablePeriods } = item;

      return R.assoc(
        'unavailablePeriods',
        R.map((activity) => {
          const acGuid = G.getGuidFromObject(activity);

          if (R.equals(acGuid, activityGuid)) return data;

          return activity;
        }, unavailablePeriods),
        item,
      );
    }

    return item;
  };

  const truckMapper = (item) => {
    const guid = G.getGuidFromObject(item);

    if (R.equals(guid, truckGuid)) {
      const { primaryDriverUnavailablePeriods, secondaryDriverUnavailablePeriods } = item;

      return {
        ...item,
        primaryDriverUnavailablePeriods: R.map((activity) => {
          const acGuid = G.getGuidFromObject(activity);

          if (R.equals(acGuid, activityGuid)) return data;

          return activity;
        }, R.or(primaryDriverUnavailablePeriods, [])),
        secondaryDriverUnavailablePeriods: R.map((activity) => {
          const acGuid = G.getGuidFromObject(activity);

          if (R.equals(acGuid, activityGuid)) return data;

          return activity;
        }, R.or(secondaryDriverUnavailablePeriods, [])),
      };
    }

    return item;
  };

  const mapperToUse = G.isTrue(dispatchByTruck) ? truckMapper : mapper;

  if (G.isAllTrue(
    G.isTrue(dispatchByTruck),
    G.isNotNilAndNotEmpty(groupGuid),
    R.equals(groupBy, GROUP_TYPE_GROUP_AND_DISPATCHER),
  )) {
    const groupStateProp = 'loadsByDispatchGroupAndDispatchers';

    const newGroupLoads = P.$map(
      mapperToUse,
      `${groupStateProp}.${groupGuid}.${dispatcherGuid}`,
      state,
    );

    return P.$set(`${groupStateProp}.${groupGuid}.${dispatcherGuid}`, newGroupLoads, state);
  }

  if (R.and(G.isNotNilAndNotEmpty(groupBy), G.isNotNilAndNotEmpty(groupGuid))) {
    const groupStateProp = G.ifElse(
      R.equals(groupBy, GROUP_TYPE_DISPATCHER),
      'driverLoadsByDispatcher',
      'loadsByDispatchGroup',
    );

    const newGroupLoads = P.$map(
      mapper,
      `${groupStateProp}.${groupGuid}`,
      state,
    );

    return P.$set(`${groupStateProp}.${groupGuid}`, newGroupLoads, state);
  }

  const newLoads = P.$map(
    mapperToUse,
    'notGroupedAssignedLoads',
    state,
  );

  const newItems = P.$map(
    mapperToUse,
    'notGroupedAssignedLoadsPagination.items',
    state,
  );

  return P.$all(
    P.$set('notGroupedAssignedLoads', newLoads),
    P.$set('notGroupedAssignedLoadsPagination.items', newItems),
    state,
  );
};

const removeUnavailablePeriodSuccess = (state, data) => {
  const { groupBy, dispatchByTruck } = state;

  const { groupGuid, truckGuid, driverGuid, primaryDriver, dispatcherGuid } = data;

  const activityGuid = G.getGuidFromObject(data);

  const mapper = (item) => {
    const { unavailablePeriods } = item;

    const drGuid = G.getGuidFromObject(item);

    if (R.equals(drGuid, driverGuid)) {
      return R.assoc(
        'unavailablePeriods',
        R.reject(R.propEq(activityGuid, GC.FIELD_GUID), unavailablePeriods),
        item,
      );
    }

    return item;
  };

  const truckMapper = (item) => {
    const { primaryDriverUnavailablePeriods, secondaryDriverUnavailablePeriods } = item;

    const drGuid = G.getGuidFromObject(item);

    if (R.equals(drGuid, truckGuid)) {
      if (G.isTrue(primaryDriver)) {
        return R.assoc(
          'primaryDriverUnavailablePeriods',
          R.reject(R.propEq(activityGuid, GC.FIELD_GUID), primaryDriverUnavailablePeriods),
          item,
        );
      }

      return R.assoc(
        'secondaryDriverUnavailablePeriods',
        R.reject(R.propEq(activityGuid, GC.FIELD_GUID), secondaryDriverUnavailablePeriods),
        item,
      );
    }

    return item;
  };

  const mapperToUse = G.isTrue(dispatchByTruck) ? truckMapper : mapper;

  if (G.isAllTrue(
    G.isTrue(dispatchByTruck),
    G.isNotNilAndNotEmpty(groupGuid),
    R.equals(groupBy, GROUP_TYPE_GROUP_AND_DISPATCHER),
  )) {
    const groupStateProp = 'loadsByDispatchGroupAndDispatchers';

    const newGroupLoads = P.$map(
      mapperToUse,
      `${groupStateProp}.${groupGuid}.${dispatcherGuid}`,
      state,
    );

    return P.$set(`${groupStateProp}.${groupGuid}.${dispatcherGuid}`, newGroupLoads, state);
  }

  if (R.and(G.isNotNilAndNotEmpty(groupBy), G.isNotNilAndNotEmpty(groupGuid))) {
    const groupStateProp = G.ifElse(
      R.equals(groupBy, GROUP_TYPE_DISPATCHER),
      'driverLoadsByDispatcher',
      'loadsByDispatchGroup',
    );

    const newGroupLoads = P.$map(
      mapperToUse,
      `${groupStateProp}.${groupGuid}`,
      state,
    );

    return P.$set(`${groupStateProp}.${groupGuid}`, newGroupLoads, state);
  }

  const newLoads = P.$map(
    mapperToUse,
    'notGroupedAssignedLoads',
    state,
  );

  const newItems = P.$map(
    mapperToUse,
    'notGroupedAssignedLoadsPagination.items',
    state,
  );

  return P.$all(
    P.$set('notGroupedAssignedLoads', newLoads),
    P.$set('notGroupedAssignedLoadsPagination.items', newItems),
    state,
  );
};

const updateTripDriverCardColorSuccess = (state, data) => {
  const { groupBy, dispatchByTruck } = state;

  const { color, tripGuid, groupGuid, truckGuid, driverGuid, dispatcherGuid } = data;

  const mapper = (item) => {
    const guid = G.getGuidFromObject(item);

    if (R.equals(guid, driverGuid)) {
      const { tels } = item;

      return R.assoc(
        'tels',
        R.map((trip) => {
          const tGuid = G.getGuidFromObject(trip);

          if (R.equals(tGuid, tripGuid)) return R.assoc(GC.FIELD_DRIVER_CARD_COLOR, color, trip);

          return trip;
        }, tels),
        item,
      );
    }

    return item;
  };

  const truckMapper = (item) => {
    const guid = G.getGuidFromObject(item);

    if (R.equals(guid, truckGuid)) {
      const { tels } = item;

      return R.assoc(
        'tels',
        R.map((trip) => {
          const tGuid = G.getGuidFromObject(trip);

          if (R.equals(tGuid, tripGuid)) return R.assoc(GC.FIELD_DRIVER_CARD_COLOR, color, trip);

          return trip;
        }, tels),
        item,
      );
    }

    return item;
  };

  const mapperToUse = G.isTrue(dispatchByTruck) ? truckMapper : mapper;

  if (G.isAllTrue(
    G.isTrue(dispatchByTruck),
    G.isNotNilAndNotEmpty(groupGuid),
    R.equals(groupBy, GROUP_TYPE_GROUP_AND_DISPATCHER),
  )) {
    const groupStateProp = 'loadsByDispatchGroupAndDispatchers';

    const newGroupLoads = P.$map(
      mapperToUse,
      `${groupStateProp}.${groupGuid}.${dispatcherGuid}`,
      state,
    );

    return P.$set(`${groupStateProp}.${groupGuid}.${dispatcherGuid}`, newGroupLoads, state);
  }

  if (R.and(G.isNotNilAndNotEmpty(groupBy), G.isNotNilAndNotEmpty(groupGuid))) {
    const groupStateProp = G.ifElse(
      R.equals(groupBy, GROUP_TYPE_DISPATCHER),
      'driverLoadsByDispatcher',
      'loadsByDispatchGroup',
    );

    const newGroupLoads = P.$map(
      mapperToUse,
      `${groupStateProp}.${groupGuid}`,
      state,
    );

    return P.$set(`${groupStateProp}.${groupGuid}`, newGroupLoads, state);
  }

  const newLoads = P.$map(
    mapperToUse,
    'notGroupedAssignedLoads',
    state,
  );

  const newItems = P.$map(
    mapperToUse,
    'notGroupedAssignedLoadsPagination.items',
    state,
  );

  return P.$all(
    P.$set('notGroupedAssignedLoads', newLoads),
    P.$set('notGroupedAssignedLoadsPagination.items', newItems),
    state,
  );
};

// tel notification socket
const telNotificationReceived = (state, data) => (
  G.storeTelNotificationReceived(state, data)
);

const getTelNotificationsSuccess = (state, data) => (
  G.storeGetTelNotificationsSuccess(state, data)
);

const hideTelNotificationsSuccess = (state, guid) => (
  G.storeHideTelNotificationsSuccess(state, guid)
);

const markAsReadTelNotificationSuccess = (state, { guid, objectGuid }) => (
  P.$set(`telNotifications.${objectGuid}.${guid}.read`, true, state)
);

// driver cards socket
const getGroupedStateOnReceived = (param) => {
  const { state, mapper, groupBy, driverGuid } = param;

  const groupStateProp = G.ifElse(
    R.equals(groupBy, GROUP_TYPE_DISPATCHER),
    'driverLoadsByDispatcher',
    'loadsByDispatchGroup',
  );

  const groupGuid = R.compose(
    R.prop('groupByGuid'),
    R.find(R.propEq(driverGuid, GC.FIELD_GUID)),
    R.reduce(R.concat, []),
    R.values,
  )(G.getPropFromObject(groupStateProp, state));

  if (G.isNotNilAndNotEmpty(groupGuid)) {
    const newGroupLoads = P.$map(
      mapper,
      `${groupStateProp}.${groupGuid}`,
      state,
    );

    return P.$set(`${groupStateProp}.${groupGuid}`, newGroupLoads, state);
  }

  return state;
};

const driverCardsTelDeleteReceived = (state, body) => {
  const { groupBy } = state;

  const { data, driverGuid } = body;

  const telGuid = G.getGuidFromObject(data);

  const mapper = (item) => {
    const { tels } = item;

    const drGuid = G.getGuidFromObject(item);

    if (R.equals(drGuid, driverGuid)) {
      return R.assoc(
        'tels',
        R.reject(R.propEq(telGuid, GC.FIELD_GUID), tels),
        item,
      );
    }

    return item;
  };

  if (R.and(G.isNotNilAndNotEmpty(groupBy), G.notEquals(groupBy, GROUP_TYPE_NONE))) {
    return getGroupedStateOnReceived({ state, mapper, groupBy, driverGuid });
  }

  const newLoads = P.$map(
    mapper,
    'notGroupedAssignedLoads',
    state,
  );

  const newItems = P.$map(
    mapper,
    'notGroupedAssignedLoadsPagination.items',
    state,
  );

  return P.$all(
    P.$set('notGroupedAssignedLoads', newLoads),
    P.$set('notGroupedAssignedLoadsPagination.items', newItems),
    state,
  );
};

const driverCardsTelReceived = (state, body) => {
  const { groupBy } = state;

  const { data, driverGuid, entityOperation } = body;

  if (R.equals(entityOperation, 'DELETE')) {
    return driverCardsTelDeleteReceived(state, body);
  }

  const mapper = (item) => {
    const guid = G.getGuidFromObject(item);

    if (R.equals(guid, driverGuid)) return P.$add('tels', data, item);

    return item;
  };

  if (R.and(G.isNotNilAndNotEmpty(groupBy), G.notEquals(groupBy, GROUP_TYPE_NONE))) {
    return getGroupedStateOnReceived({ state, mapper, groupBy, driverGuid });
  }

  const newLoads = P.$map(
    mapper,
    'notGroupedAssignedLoads',
    state,
  );

  const newItems = P.$map(
    mapper,
    'notGroupedAssignedLoadsPagination.items',
    state,
  );

  return P.$all(
    P.$set('notGroupedAssignedLoads', newLoads),
    P.$set('notGroupedAssignedLoadsPagination.items', newItems),
    state,
  );
};
//

export default createReducer({
  [A.setZoomToStore]: setZoomToStore,
  [A.setValueToStore]: setValueToStore,
  [A.setInitialState]: setInitialState,
  [A.setFilterToStore]: setFilterToStore,
  [A.setFiltersToStore]: setFiltersToStore,
  [A.setGroupByToStore]: setGroupByToStore,
  [A.setDayFilterToStore]: setDayFilterToStore,
  [A.setDateRangeFilters]: setDateRangeFilters,
  [A.setSearchDateToStore]: setSearchDateToStore,
  [A.setSearchRadiusValue]: setSearchRadiusValue,
  [A.setGlobalFilterValue]: setGlobalFilterValue,
  [A.setLoadRefFilterValue]: setLoadRefFilterValue,
  [A.setMinEmptyHoursValue]: setMinEmptyHoursValue,
  [A.toggleUnassignedLoads]: toggleUnassignedLoads,
  [A.toggleDispatchByTruck]: toggleDispatchByTruck,
  [A.getDriverCardsInfoSuccess]: getDriverCardsInfoSuccess,
  [A.filterByPlaceAutocomplete]: filterByPlaceAutocomplete,
  [A.getUnassignedLoadsSuccess]: getUnassignedLoadsSuccess,
  [A.setSearchByCustomerFilter]: setSearchByCustomerFilter,
  [A.getTelNotificationsSuccess]: getTelNotificationsSuccess,
  [A.hideTelNotificationsSuccess]: hideTelNotificationsSuccess,
  [A.createPendingActivitySuccess]: createPendingActivitySuccess,
  [A.updatePendingActivitySuccess]: updatePendingActivitySuccess,
  [A.removePendingActivitySuccess]: removePendingActivitySuccess,
  [A.getLoadsByDispatchGroupSuccess]: getLoadsByDispatchGroupSuccess,
  [A.getMoreNotGroupedAssignedLoads]: getMoreNotGroupedAssignedLoads,
  [A.createUnavailablePeriodSuccess]: createUnavailablePeriodSuccess,
  [A.updateUnavailablePeriodSuccess]: updateUnavailablePeriodSuccess,
  [A.removeUnavailablePeriodSuccess]: removeUnavailablePeriodSuccess,
  [A.setSearchPlaceAutocompleteValue]: setSearchPlaceAutocompleteValue,
  [A.markAsReadTelNotificationSuccess]: markAsReadTelNotificationSuccess,
  [A.createDriverOnDriversCardSuccess]: createDriverOnDriversCardSuccess,
  [A.updateTripDriverCardColorSuccess]: updateTripDriverCardColorSuccess,
  [A.getDriverCardsByTruckInfoSuccess]: getDriverCardsByTruckInfoSuccess,
  [A.getDriverLoadsByDispatcherSuccess]: getDriverLoadsByDispatcherSuccess,
  [A.getNotGroupedAssignedLoadsSuccess]: getNotGroupedAssignedLoadsSuccess,
  [A.dropLoadsByDispatchGroupAndDispatchersGroup]: dropLoadsByDispatchGroupAndDispatchersGroup,
  [A.getLoadsByDispatchGroupAndDispatcherSuccess]: getLoadsByDispatchGroupAndDispatcherSuccess,
  //
  [socketTelWarningReceived]: telNotificationReceived,
  [socketDriverCardsTelReceived]: driverCardsTelReceived,
}, initialState);

